import { useMemo, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@src/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/components/ui/select'
import { Skeleton } from '@src/components/ui/skeleton'
import { getRiskProfileSummaryColumns } from '../constants/RiskProfileSummaryTable.columns'
import type { RiskProfileSummaryRow } from '../utils/riskProfileSummary.utils'

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const

interface RiskProfileSummaryTableProps {
  rows: RiskProfileSummaryRow[]
  labelHeader: string
  isLoading?: boolean
}

export function RiskProfileSummaryTable({
  rows,
  labelHeader,
  isLoading = false,
}: RiskProfileSummaryTableProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'damageCost', desc: true }])
  const columns = useMemo(() => getRiskProfileSummaryColumns(labelHeader), [labelHeader])

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE_OPTIONS[0] } },
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    )
  }

  const { pageIndex, pageSize } = table.getState().pagination
  const totalRows = table.getFilteredRowModel().rows.length
  const shownCount = table.getRowModel().rows.length
  const showPaginationControls = totalRows > PAGE_SIZE_OPTIONS[0]

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table className="border-spacing-0 border-collapse rounded-lg overflow-hidden">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-neutral-100">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex gap-2">
        {showPaginationControls && (
          <Select
            value={String(pageSize)}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  Show top {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {shownCount} of {totalRows} shown
          </span>
          {showPaginationControls && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="text-primary disabled:text-muted-foreground"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </button>
              <button
                type="button"
                className="text-primary disabled:text-muted-foreground"
                onClick={() => table.nextPage()}
                disabled={pageIndex + 1 >= table.getPageCount()}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
